import comm from "csi/comm";
import csi from "csi/main";
import locale from "csi/locale";
import New from "csi/table/New";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import RetailProductSKUForm from "./RetailProductSKUForm";

const NewRetailProductSKUController = declare(New, {
	// Boolean: default New controller only adds one node - this controller may add many
	bUpdateParent: false,

	successMsg: "",

	/**
     * @override
     * create SKUs according to form and add to source
     */
	toWriter() {
		this.writeSKUs();
	},

	/**
     * @override
     * disable Save & Go since action can create multiple SKUs
     */
	_canNavigate() {
		return false;
	},

	/** create skus of selected variants */ 
	writeSKUs() {
		const w = this.m_writer;
		const fd = this.m_formData;
		const form = this.popupView as typeof RetailProductSKUForm.prototype;
		const prefix = fd.getFieldValue(xml.ATTR_NAME, true);
		const skus = [];
		const gtin = fd.getFieldValue("AutoGTIN", true);
        
		// use view to get field value in case field is hidden
		const nameField = fd.getField(xml.ATTR_NAME);
		const nameHasExpr = (nameField && nameField.hasExpression());

		// see which SKUs already exist for the parent
		const existingSKUs = form.getExistingSKUs();
		let skusCreated = 0;
		let skusIgnored = 0;

		const createSkus = (variant): void => {
			// see if variant is already taken - ignore check if parent is no-variant
			const variantKey = form.getVariantSKUHashKey(variant);
			if (variantKey != csi.EMPTY_URL && existingSKUs[variantKey]) {
				// this combination is already taken - no dups allowed
				skusIgnored ++;
				return;
			}

			// Variant will now be already taken - remember for next time
			existingSKUs[variantKey] = true;

			const type = "RetailProductSKU";
			const url = comm.createUrl();

			// create new SKU node, cn is a ChangeNode
			const cn = w.createNode(url, type);
			this.m_urlNode = url;
			this.toWriterFields(url, fd);

			// see if there is an expression on Node Name set by the Business Object - if so, ignore this next part
			if (nameField && !nameHasExpr) {
				// Found that any string in double quotes were resulting in error while evaluating expression.Added this unicode replacement to fix that.
				let exp = "\"" + prefix.replace(/\"/g, "\\u0022") + "\"";

				// populate variant attributes
				if (variant) {
					exp += "+ \"-\" + RealizedVariant.`Node Name`";
				}
				// use one-time expression for Name
				cn.changeAttributeExp("Node Name", exp, true);
			}

			cn.changeAttribute("RealizedVariant", xml.ATTR_REF, variant || csi.EMPTY_URL);
			cn.attach(this.m_urlParent, "RealizedProducts");

			skusCreated ++;
			skus.push(url);
		};

		// get selected variants
		const selectedVariants = form.getSelectedVariants();
		selectedVariants.forEach((selectedVariants) => {
			createSkus(selectedVariants);
		});
        
		if (gtin) {
			skus.forEach((sku) => {
				w.callMethod("SiteAdmin", "GlobalTradeItemAutoGenerateGTIN")
					.addParameter(xml.PROP_URL_PARENT, xml.ATTR_REF, this.m_urlParent)
					.addParameter(xml.PROP_URL, xml.ATTR_REF, sku);	
			});
		}

		//  generate success message
		let msg = "";
		if (skusCreated == 1) {
			msg += locale.resource("RetailProductSKUCreated", [skusCreated]);
		}
		if (skusCreated > 1) {
			msg += locale.resource("RetailProductSKUsCreated", [skusCreated]);
		}
		if (skusIgnored > 0) {
			if (msg != "") {
				msg += ", ";
			}
			if (skusIgnored == 1) {
				msg += locale.resource("RetailProductSKUIgnored", [skusIgnored]);
			}
			if (skusIgnored > 1) {
				msg += locale.resource("RetailProductSKUsIgnored", [skusIgnored]);
			}
		}

		this.successMsg = msg;

		return skus;
	},

	/**
     * @override
     * add new url to request
     */
	getReturnNodeRequest(urlsNew) {
		if (!this.getParent().RealizedProducts) {
			return this.inherited(arguments);
		}
		return this.inherited(arguments, [urlsNew.concat(this.getParent().RealizedProducts)]);
	},

	/**
     * @override
     * add new nodes to form cache in case using Save & New - needed to support getExistingSKUs
     */
	postProcessSuccess() {
		this.popupView.cacheView.cacheResponse(this.m_response);
		return this.inherited(arguments);
	},

	/**
     * @override
     * retrieve the nodes that were created - needed to reflect changes on table
     */
	getNewNodes() {
		const nodes = this.m_cache.getNodes(this.getParent().RealizedProducts);
		return nodes;
	},
});

export = NewRetailProductSKUController;